import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"
import { translations } from "../../../configs/translations"
import { Language } from "../../../context/LanguageContext"
import { Vehicle } from "../../../models/Vehicle"
import { EventSourceConnection } from '../../../services/EventSourceConnection'
import RaceService from "../../../services/RaceService"
import { LoaderIcon } from "../../preloader/LoaderIcon/LoaderIcon"
import { GhostRideOffer, SearchWithGhostOffer } from "./search-with-ghost-offer/SearchWithGhostOffer"
import "./SearchRace.css"

export interface PlayersInSearchData {
    playersByRankAndType: number
    playersByRank: number
    playersInSearch: number
    startedSearchMillis: number
    vehicleRank: number
    ghostRideOffer: GhostRideOffer | null
}

export const SearchRace = ({
    selectedVehicle,
    withButton,
    onFreeroamButtonClick,
    onCancelButtonClick,
    setShow
}: {
    selectedVehicle?: Vehicle | null,
    withButton?: ('cancel' | 'freeroam')[],
    onFreeroamButtonClick?: () => void,
    onCancelButtonClick?: () => void,
    setShow?: React.Dispatch<React.SetStateAction<boolean>>
}) => {
    const [language, setLanguage] = useState<Language>('ru')
    const t = translations[language]
    const [timeSearch, setTimeSearch] = useState<number>(0)
    const [playersByRankAndType, setPlayersByRankAndType] = useState<number>(0)
    const [playersByRank, setPlayersByRank] = useState<number>(0)
    const [playersInSearch, setPlayersInSearch] = useState<number>(0)
    const [vehicleRank, setVehicleRank] = useState<number>(selectedVehicle?.rank || 0)
    const [isReadyToShow, setIsReadyToShow] = useState<boolean>(false)
    const [ghostOffer, setGhostOffer] = useState<GhostRideOffer | null>(null)
    const hadGhostOffer = useRef(false)

    const setPlayersInSearchData = (data: PlayersInSearchData) => {
        setPlayersByRankAndType(data.playersByRankAndType)
        setPlayersByRank(data.playersByRank)
        setPlayersInSearch(data.playersInSearch)
        if (data.startedSearchMillis) {
            setTimeSearch(getDifferenceInSecs(data.startedSearchMillis))
        }
        setVehicleRank(selectedVehicle?.rank || data.vehicleRank)
        setGhostOffer(data.ghostRideOffer ?? null)
    }

    const getDifferenceInSecs = (startedAt: number) => {
        const now = new Date()
        const diff = now.getTime() - startedAt
        return Math.floor(diff / 1000)
    }

    useEffect(() => {
        let sse: EventSourceConnection | null = null
        setIsReadyToShow?.(false)
        RaceService.getIsPlayerInSearch()
            .then(isInSearch => {
                if (!isInSearch) {
                    setShow?.(false)
                    return
                }
                RaceService.getUsersInSearch()
                    .then((response) => {
                        setPlayersInSearchData(response)
                        sse = new EventSourceConnection(RaceService.getRaceSearchDataSse)
                        sse.registerListener<PlayersInSearchData>("RACE_SEARCH_UPDATE", setPlayersInSearchData)
                        setIsReadyToShow(true)
                    })
                    .catch((error) => {
                        toast.error(
                            error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                                ? t[error.response.data as keyof typeof t]
                                : t.error_unknown,
                            { className: 'notification-error' }
                        )
                    })
            })

        return () => sse?.close()

    }, [])

    useEffect(() => {
        if (hadGhostOffer.current !== !!ghostOffer) {
            hadGhostOffer.current = !!ghostOffer
        }
    }, [ghostOffer])

    useEffect(() => {
        const timeSearchInterval = setInterval(() => {
            setTimeSearch(prev => prev + 1)
        }, 1000)
        return () => clearInterval(timeSearchInterval)
    }, [])

    const getTime = () => {
        let remainingSec = timeSearch
        const hours = Math.floor(remainingSec / 3600)
        remainingSec %= 3600
        const mins = Math.floor(remainingSec / 60)
        const secs = remainingSec % 60
        const pad = (num: number) => String(num).padStart(2, '0')
        return `${pad(hours)}:${pad(mins)}:${pad(secs)}`
    }

    const stopSearch = () => {
        RaceService.removePlayerFromRaceSearch()
            .then(() => {
                setShow?.(false)
            })
            .catch((error) => {
                toast.error(
                    error?.response?.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: "notification-error" }
                )
            })
    }

    return (
        <>
            <AnimatePresence mode="wait">
                {isReadyToShow && ghostOffer &&
                    <SearchWithGhostOffer
                        key="ghost-offer"
                        offer={ghostOffer}
                        playersByRankAndType={playersByRankAndType}
                        withCancelButton={withButton?.includes("cancel")}
                        onCancelSearchClick={() => {
                            onCancelButtonClick?.()
                            stopSearch()
                        }}
                        onClose={() => setGhostOffer(null)}
                    />}
                {isReadyToShow && !ghostOffer &&
                    <motion.div
                        key="search-race"
                        className="search-race"
                        initial={{ x: '120%', opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: '120%', opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 80, damping: 18 }}
                    >
                        {withButton?.includes("cancel") &&
                            <div
                                className='search-race-cancel-button'
                                onClick={() => {
                                    onCancelButtonClick?.()
                                    stopSearch()
                                }}
                            >
                                <img src="./icons/32px/cancel.svg" alt="cancel" />
                                <p>{t.cancel_search}</p>
                            </div>
                        }
                        <div className='search-race-content-wrapper'>
                            <div className="search-race-content">
                                <LoaderIcon />
                                <div className="search-race-text-container">
                                    <p className="search-race-text">{t.players_in_search_by_rank_and_car}: <span className="data">{playersByRankAndType}</span></p>
                                    <p className="search-race-text">{t.players_in_search_by_rank}: <span className="data">{playersByRank}</span></p>
                                    <p className="search-race-text">{t.players_in_search}: <span className="data">{playersInSearch}</span></p>
                                    <p className="search-race-text">{t.vehicle_rank}: <span className="data">{vehicleRank || 0}</span></p>
                                    <p className="search-race-text">{t.time_search}: <span className="data">{getTime()}</span></p>
                                </div>
                            </div>
                            <div className="search-race-buttons">
                                {withButton?.includes("freeroam") &&
                                    <button
                                        onClick={() => onFreeroamButtonClick?.()}
                                        className="search-race-additional-button"
                                    >
                                        {t.wait_in_freeroam}
                                    </button>}
                            </div>
                        </div>
                    </motion.div>}
            </AnimatePresence>
        </>
    )
}